import { useEffect, useState, useMemo, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import {
  CalendarBlank,
  Clock,
  MapPin,
  CaretLeft,
  CaretRight,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { ArrowUpRight } from "assets/icons";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import {
  DashboardBlock,
  DashboardBlockHeader,
  DashboardBlockContent,
} from "../layout/DashboardBlock";
import { BlockLoadingState, BlockEmptyState } from "../states/BlockStates";
import { blockStyles, cx } from "../utils/blockStyles.utils";
import {
  OWNER_STYLES,
  SharedCalendarEvent,
  inferEventType,
} from "screens/sharedCalendar/types";

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const dayDiffInDays = (a: Date, b: Date): number => {
  const aDay = startOfDay(a).getTime();
  const bDay = startOfDay(b).getTime();
  return Math.round((aDay - bDay) / DAY_MS);
};

const NL_WEEKDAYS = [
  "zondag",
  "maandag",
  "dinsdag",
  "woensdag",
  "donderdag",
  "vrijdag",
  "zaterdag",
];

const formatDdMmYyyy = (d: Date): string => {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const dayLabel = (selected: Date): string => {
  const today = new Date();
  const diff = dayDiffInDays(selected, today);
  if (diff === 0) return "Vandaag";
  if (diff === 1) return "Morgen";
  if (diff === -1) return "Gisteren";
  // Capitalize first letter of weekday
  const weekday = NL_WEEKDAYS[selected.getDay()];
  return weekday.charAt(0).toUpperCase() + weekday.slice(1);
};

const formatTimeRange = (event: SharedCalendarEvent): string => {
  if (event.isAllDay) return "Hele dag";
  const start = new Date(event.start);
  const end = new Date(event.end);
  const fmt = (d: Date) =>
    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${fmt(start)} – ${fmt(end)}`;
};

export const AppointmentsTodayBlockNew = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));
  const [events, setEvents] = useState<SharedCalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchEvents = useCallback(
    async (target: Date) => {
      if (!session) return;
      setIsLoading(true);
      try {
        const startISO = startOfDay(target).toISOString();
        const endISO = endOfDay(target).toISOString();
        const response = await fetchWithAuth(
          `${API_URL}/shared-calendar/events?start=${encodeURIComponent(
            startISO,
          )}&end=${encodeURIComponent(endISO)}`,
          { method: "GET" },
          session,
        );
        if (!response.ok) {
          console.error(
            "Failed to fetch appointments for day:",
            response.status,
            response.statusText,
          );
          setEvents([]);
          return;
        }
        const data = await response.json();
        if (!Array.isArray(data)) {
          console.error("Expected array of events, got:", data);
          setEvents([]);
          return;
        }
        setEvents(data);
      } catch (error) {
        console.error("Error fetching appointments:", error);
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    },
    [session?.id],
  );

  useEffect(() => {
    fetchEvents(selectedDate);
  }, [fetchEvents, selectedDate]);

  const goPrev = () =>
    setSelectedDate((d) => startOfDay(new Date(d.getTime() - DAY_MS)));
  const goNext = () =>
    setSelectedDate((d) => startOfDay(new Date(d.getTime() + DAY_MS)));
  const goToday = () => setSelectedDate(startOfDay(new Date()));

  const isToday = useMemo(
    () => isSameDay(selectedDate, new Date()),
    [selectedDate],
  );

  // Sorted by start time, with all-day events first
  const sortedEvents = useMemo(
    () =>
      [...events].sort((a, b) => {
        if (a.isAllDay && !b.isAllDay) return -1;
        if (!a.isAllDay && b.isAllDay) return 1;
        return new Date(a.start).getTime() - new Date(b.start).getTime();
      }),
    [events],
  );

  const ownerCounts = useMemo(() => {
    const counts: Record<"ella" | "leo", number> = { ella: 0, leo: 0 };
    events.forEach((e) => {
      if (e.owner === "ella" || e.owner === "leo") counts[e.owner] += 1;
    });
    return counts;
  }, [events]);

  const titleLabel = useMemo(() => dayLabel(selectedDate), [selectedDate]);
  const dateLabel = useMemo(() => formatDdMmYyyy(selectedDate), [selectedDate]);

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={
          <CalendarBlank size={20} weight="duotone" className="text-moss-primary" />
        }
        title={`${titleLabel} in de agenda`}
        action={
          <button
            onClick={() => navigate("/agenda")}
            className={cx(blockStyles.header.action, "flex items-center gap-1")}
            title="Open volledige agenda"
          >
            Agenda openen
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        }
      />
      <DashboardBlockContent padding="md">
        {/* Day navigator — always shows the explicit dd/mm/yyyy date */}
        <div className="border-border mb-3 flex items-center justify-between gap-2 rounded-md border bg-white px-2 py-1.5">
          <button
            onClick={goPrev}
            className="text-neutral-secondary-mid hover:text-neutral-primary hover:bg-neutral-100 rounded p-1 transition-colors"
            aria-label="Vorige dag"
            title="Vorige dag"
          >
            <CaretLeft size={16} />
          </button>
          <div className="flex min-w-0 flex-1 flex-col items-center text-center leading-tight">
            <span className="text-neutral-primary text-sm font-semibold">
              {titleLabel}
            </span>
            <span className="text-neutral-secondary-mid text-[11px] tabular-nums">
              {dateLabel}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {!isToday && (
              <button
                onClick={goToday}
                className="text-moss-primary hover:bg-moss-primary/10 rounded p-1 transition-colors"
                aria-label="Naar vandaag"
                title="Terug naar vandaag"
              >
                <ArrowCounterClockwise size={14} />
              </button>
            )}
            <button
              onClick={goNext}
              className="text-neutral-secondary-mid hover:text-neutral-primary hover:bg-neutral-100 rounded p-1 transition-colors"
              aria-label="Volgende dag"
              title="Volgende dag"
            >
              <CaretRight size={16} />
            </button>
          </div>
        </div>

        {isLoading ? (
          <BlockLoadingState message="Agenda laden..." />
        ) : sortedEvents.length === 0 ? (
          <BlockEmptyState
            icon={<CalendarBlank size={32} weight="duotone" className="text-neutral-300" />}
            title={`Geen afspraken op ${dateLabel}`}
            description="Ella en Leo hebben deze dag geen geplande afspraken."
          />
        ) : (
          <div className="space-y-3">
            {/* Owner summary */}
            <div className="flex flex-wrap items-center gap-3 text-xs">
              {(["ella", "leo"] as const).map((owner) => (
                <div key={owner} className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: OWNER_STYLES[owner].accentHex }}
                    aria-hidden="true"
                  />
                  <span className="text-neutral-primary font-medium">
                    {OWNER_STYLES[owner].label}
                  </span>
                  <span className="text-neutral-secondary-mid">
                    · {ownerCounts[owner]} {ownerCounts[owner] === 1 ? "afspraak" : "afspraken"}
                  </span>
                </div>
              ))}
            </div>

            {/* Event list */}
            <ul className="divide-border divide-y">
              {sortedEvents.map((event) => {
                const ownerStyle = OWNER_STYLES[event.owner];
                const type = inferEventType(event);
                return (
                  <li key={event.id}>
                    <button
                      onClick={() => navigate("/agenda")}
                      className="hover:bg-neutral-50 group flex w-full items-start gap-3 py-2 text-left transition-colors"
                      title={event.subject}
                    >
                      {/* Owner accent stripe */}
                      <span
                        className="mt-1 inline-block h-10 w-1 shrink-0 rounded"
                        style={{ backgroundColor: ownerStyle.accentHex }}
                        aria-hidden="true"
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className="rounded-sm px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-white"
                            style={{ backgroundColor: ownerStyle.accentHex }}
                          >
                            {ownerStyle.label} · {type}
                          </span>
                        </div>
                        <p className="text-neutral-primary mt-1 truncate text-sm font-semibold">
                          {event.subject}
                        </p>
                        <div className="text-neutral-secondary-mid mt-0.5 flex flex-wrap items-center gap-3 text-[11px]">
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {formatTimeRange(event)}
                          </span>
                          {event.location && (
                            <span className="flex min-w-0 items-center gap-1">
                              <MapPin size={12} className="shrink-0" />
                              <span className="truncate">{event.location}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </DashboardBlockContent>
    </DashboardBlock>
  );
};
